import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Wrench, Loader2, Plus, Trash2, Save } from "lucide-react";

const EMPTY = { category: "", codice: "", oggetto: "", um: "", descrizione: "", prezzo: 0, costo: 0 };

export default function ListinoServizi() {
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(null);

  const load = () => api.get("/listino-servizi").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const setRow = (i, k, v) => setItems((rows) => rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const addRow = () => setItems((rows) => [{ ...EMPTY, _new: true }, ...rows]);

  const saveRow = async (i) => {
    const r = items[i];
    if (!r.oggetto.trim() || !r.category.trim()) { toast.error("Categoria e oggetto obbligatori"); return; }
    setSaving(i);
    const payload = { category: r.category, codice: r.codice, oggetto: r.oggetto, um: r.um, descrizione: r.descrizione, prezzo: Number(r.prezzo) || 0, costo: Number(r.costo) || 0 };
    try {
      const { data } = r.id ? await api.put(`/listino-servizi/${r.id}`, payload) : await api.post("/listino-servizi", payload);
      setItems((rows) => rows.map((x, idx) => (idx === i ? data : x)));
      toast.success("Servizio salvato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(null); }
  };
  const removeRow = async (i) => {
    const r = items[i];
    if (r.id) { try { await api.delete(`/listino-servizi/${r.id}`); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return; } }
    setItems((rows) => rows.filter((_, idx) => idx !== i));
    toast.success("Servizio eliminato");
  };

  return (
    <div data-testid="listino-servizi-page">
      <header className="sticky top-14 lg:top-0 z-10 h-16 border-b border-border bg-background flex items-center justify-between px-4 lg:px-8">
        <div>
          <h1 className="text-xl font-heading font-bold">Listino servizi</h1>
          <p className="text-xs text-muted-foreground">Servizi selezionabili nel Preventivatore. Tutto editabile: categoria, codice, oggetto, UM, descrizione e prezzo.</p>
        </div>
        <Button onClick={addRow} data-testid="servizio-add-btn" className="font-semibold"><Plus className="h-4 w-4 mr-1.5" /> Nuovo servizio</Button>
      </header>
      <div className="p-4 lg:p-8">
        {!items ? <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          : items.length === 0 ? <p className="text-sm text-muted-foreground py-10 text-center">Nessun servizio. Aggiungine uno con "Nuovo servizio".</p>
            : <div className="space-y-3">
              {items.map((r, i) => (
                <Card key={r.id || i} className="rounded border-border p-4" data-testid={`servizio-row-${i}`}>
                  <div className="grid sm:grid-cols-12 gap-3">
                    <div className="sm:col-span-3"><label className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Wrench className="h-3 w-3 text-primary" /> Categoria</label><Input value={r.category} onChange={(e) => setRow(i, "category", e.target.value)} data-testid={`servizio-category-${i}`} /></div>
                    <div className="sm:col-span-2"><label className="text-[10px] uppercase tracking-widest text-muted-foreground">Codice</label><Input value={r.codice} onChange={(e) => setRow(i, "codice", e.target.value)} data-testid={`servizio-codice-${i}`} /></div>
                    <div className="sm:col-span-3"><label className="text-[10px] uppercase tracking-widest text-muted-foreground">Oggetto</label><Input value={r.oggetto} onChange={(e) => setRow(i, "oggetto", e.target.value)} data-testid={`servizio-oggetto-${i}`} /></div>
                    <div className="sm:col-span-1"><label className="text-[10px] uppercase tracking-widest text-muted-foreground">UM</label><Input value={r.um} onChange={(e) => setRow(i, "um", e.target.value)} data-testid={`servizio-um-${i}`} /></div>
                    <div className="sm:col-span-1"><label className="text-[10px] uppercase tracking-widest text-muted-foreground">Costo €</label><Input type="number" step="0.01" value={r.costo ?? 0} onChange={(e) => setRow(i, "costo", e.target.value)} className={!Number(r.costo) ? "border-red-400" : ""} data-testid={`servizio-costo-${i}`} /></div>
                    <div className="sm:col-span-2"><label className="text-[10px] uppercase tracking-widest text-muted-foreground">Prezzo €</label><Input type="number" step="0.01" value={r.prezzo} onChange={(e) => setRow(i, "prezzo", e.target.value)} className={!Number(r.prezzo) ? "border-red-400" : ""} data-testid={`servizio-prezzo-${i}`} /></div>
                    <div className="sm:col-span-12"><label className="text-[10px] uppercase tracking-widest text-muted-foreground">Descrizione</label><Textarea rows={2} value={r.descrizione} onChange={(e) => setRow(i, "descrizione", e.target.value)} data-testid={`servizio-descrizione-${i}`} /></div>
                  </div>
                  <div className="flex justify-end gap-2 mt-3">
                    <Button variant="ghost" size="sm" onClick={() => removeRow(i)} className="text-destructive hover:text-destructive" data-testid={`servizio-delete-${i}`}><Trash2 className="h-4 w-4 mr-1" /> Elimina</Button>
                    <Button size="sm" onClick={() => saveRow(i)} disabled={saving === i} data-testid={`servizio-save-${i}`}>{saving === i ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Salva</Button>
                  </div>
                </Card>
              ))}
            </div>}
      </div>
    </div>
  );
}
